import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import { useReadOnly } from '../../utils/readOnly';
import { iconify } from './Icon';

// The second level of navigation inside a module, drawn as one segmented
// control so it can never be mistaken for the module tabs above it:
//
//   [ View Active | + Record New Entry | Deleted ]
//
//   <SubTabs
//     value={viewMode} onChange={switchView}
//     tabs={[{ key: 'history', label: 'View Active Records' },
//            { key: 'add', label: '+ Record New Entry', write: true },
//            { key: 'deleted', label: 'Deleted', variant: 'danger' }]}
//   />
export interface SubTabDefinition {
  key: string;
  label: ReactNode;
  /** 'danger' renders the segment in red — Deleted / Voided lists. */
  variant?: 'danger';
  /** The create/edit tab (+ Record New Entry) — hidden for a read-only user. */
  write?: boolean;
}

export interface SubTabsProps {
  tabs?: SubTabDefinition[];
  value?: string;
  onChange?: (key: string) => void;
  /** Overrides the wrapper — e.g. `{ marginBottom: 0 }` when the tabs sit in a search row. */
  style?: CSSProperties;
  isDarkMode?: boolean;
}

const SubTabs = ({ tabs = [], value, onChange, style, isDarkMode }: SubTabsProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;
  const readOnly = useReadOnly();

  // The create/edit tab only opens a form a read-only user cannot save, so it is
  // dropped for them — leaving View Active and Deleted, which are reads.
  const visibleTabs = tabs.filter((tab) => !(readOnly && tab.write));

  return (
    <div style={{ display: 'flex', marginBottom: t.spacing.lg, ...style }}>
      <div style={{
        display: 'inline-flex', flexWrap: 'wrap', gap: '2px',
        padding: '3px', borderRadius: t.radius.md,
        backgroundColor: t.bgMuted, border: `1px solid ${t.border}`
      }}>
        {visibleTabs.map((tab) => {
          const selected = value === tab.key;
          const danger = tab.variant === 'danger';
          return (
            <button
              key={tab.key}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange?.(tab.key)}
              style={{
                border: 'none',
                borderRadius: t.radius.sm,
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: t.fontSize.base,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                backgroundColor: selected ? t.bgMain : 'transparent',
                boxShadow: selected ? t.shadowCard : 'none',
                color: danger ? t.danger : (selected ? t.textMain : t.textMuted),
                fontWeight: selected ? t.fontWeight.bold : t.fontWeight.medium
              }}
            >
              {iconify(tab.label, 14)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SubTabs;
